// src/features/monetize/components/TemplatesTab.tsx
//
// One-click recipes over the catalog: a few answers seed the products,
// prices and codes, which are then edited like anything else.

import { useEffect, useMemo, useState } from 'react';
import {
  Button, Card, Divider, Group, Modal, MultiSelect, NumberInput, Select, SimpleGrid, Stack, Text, TextInput, Textarea, ThemeIcon,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconTicket, IconUsersGroup } from '@tabler/icons-react';
import { useController, useForm, useWatch, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { DateTimeField } from '@/shared/components/DateTimeField';
import { MONETIZE_CONSTRAINTS } from '@/lib/constants/monetize';
import type { GatingSync } from '@/lib/types/Monetize';
import { MonetizeUtils } from '../MonetizeUtils';
import { membershipFormSchema, ticketedLiveFormSchema, type MembershipFormValues, type TicketedLiveFormValues } from '../schemas/monetizeSchemas';
import { useApplyMembership, useApplyTicketedLive } from '../useMonetizeMutations';
import { ScopePicker, useScopeLookups } from './ScopePicker';

interface TemplatesTabProps {
  onApplied: (sync: GatingSync | undefined) => void;
}

export function TemplatesTab({ onApplied }: TemplatesTabProps) {
  const [open, setOpen] = useState<'membership' | 'ticketed_live' | null>(null);
  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">Start from a recipe. It creates the products and codes for you; edit them afterwards on the Products tab.</Text>
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <TemplateCard
          icon={<IconUsersGroup size={20} />}
          title="Membership"
          body="A monthly subscription (yearly optional) with a free trial, over the playlists you choose or everything you publish."
          onUse={() => setOpen('membership')}
        />
        <TemplateCard
          icon={<IconTicket size={20} />}
          title="Ticketed live event"
          body="A pass for a live stream, on sale now, playable through the event and a replay window. The recording is covered automatically."
          onUse={() => setOpen('ticketed_live')}
        />
      </SimpleGrid>
      <MembershipModal opened={open === 'membership'} onClose={() => setOpen(null)} onApplied={onApplied} />
      <TicketedLiveModal opened={open === 'ticketed_live'} onClose={() => setOpen(null)} onApplied={onApplied} />
    </Stack>
  );
}

function TemplateCard({ icon, title, body, onUse }: { icon: React.ReactNode; title: string; body: string; onUse: () => void }) {
  return (
    <Card shadow="sm" padding="lg" withBorder>
      <Stack gap="sm">
        <Group gap="sm">
          <ThemeIcon variant="light" size="lg">{icon}</ThemeIcon>
          <Text fw={600}>{title}</Text>
        </Group>
        <Text size="sm" c="dimmed">{body}</Text>
        <Button variant="light" onClick={onUse} style={{ alignSelf: 'flex-start' }}>Use this template</Button>
      </Stack>
    </Card>
  );
}

// ── Membership ───────────────────────────────────────────────────

const membershipDefaults: MembershipFormValues = { name: 'Members', description: '', wholeService: false, scopes: [], monthlyAmount: 9.99, yearlyAmount: 79, trialDays: 7 };

function MembershipModal({ opened, onClose, onApplied }: { opened: boolean; onClose: () => void; onApplied: (sync: GatingSync | undefined) => void }) {
  const { selectedCId } = useAuth();
  const apply = useApplyMembership();
  const form = useForm<MembershipFormValues>({ resolver: zodResolver(membershipFormSchema), defaultValues: membershipDefaults });

  useEffect(() => {
    if (opened) form.reset(membershipDefaults);
  }, [opened, form]);

  async function handleSubmit(v: MembershipFormValues) {
    if (!selectedCId) return;
    try {
      const res = await apply.mutateAsync({
        name: v.name.trim(),
        description: v.description.trim(),
        scopes: v.wholeService ? [MonetizeUtils.scopeToken('service', selectedCId)] : v.scopes,
        monthlyAmount: MonetizeUtils.dollarsToCents(v.monthlyAmount),
        yearlyAmount: typeof v.yearlyAmount === 'number' ? MonetizeUtils.dollarsToCents(v.yearlyAmount) : 0,
        trialDays: v.trialDays,
      });
      notifications.show({ title: 'Membership created', message: `"${v.name.trim()}" is active and on sale.`, color: 'green' });
      onApplied(res.gatingSync);
      onClose();
    } catch (err) {
      notifications.show({ title: 'Error', message: MonetizeUtils.errorMessage(err, 'Failed to create the membership.'), color: 'red' });
    }
  }

  type C = Control<MembershipFormValues>;
  const c = form.control as C;
  return (
    <Modal opened={opened} onClose={onClose} title="Membership" size="lg">
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextField control={c} name="name" label="Name" description="What viewers see, for example “Members” or “All Access”" />
          <TextAreaField control={c} name="description" label="Description" />
          <Divider label="Members get" labelPosition="left" />
          <MembershipScopes control={c} />
          <Divider label="Prices" labelPosition="left" />
          <Group grow align="flex-start">
            <MoneyField control={c} name="monthlyAmount" label="Monthly" />
            <MoneyField control={c} name="yearlyAmount" label="Yearly (optional)" />
            <TrialField control={c} />
          </Group>
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button type="submit" loading={apply.isPending}>Create membership</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function MembershipScopes({ control }: { control: Control<MembershipFormValues> }) {
  const scopes = useController({ control, name: 'scopes' });
  const whole = useController({ control, name: 'wholeService' });
  return <ScopePicker value={scopes.field.value} onChange={scopes.field.onChange} wholeService={whole.field.value} onWholeServiceChange={whole.field.onChange} error={scopes.fieldState.error?.message} />;
}

function TrialField({ control }: { control: Control<MembershipFormValues> }) {
  const { field, fieldState } = useController({ control, name: 'trialDays' });
  return <NumberInput label="Free trial" suffix=" days" min={0} max={MONETIZE_CONSTRAINTS.MAX_TRIAL_DAYS} allowDecimal={false} value={field.value} onChange={(v) => field.onChange(typeof v === 'number' ? v : 0)} error={fieldState.error?.message} />;
}

// ── Ticketed live ────────────────────────────────────────────────

const ticketedDefaults: TicketedLiveFormValues = { name: '', description: '', liveMediaId: '', listIds: [], eventStartsAt: null, eventEndsAt: null, replayHours: 72, amount: 14.99, promoCode: '', promoPercentOff: 0 };

function TicketedLiveModal({ opened, onClose, onApplied }: { opened: boolean; onClose: () => void; onApplied: (sync: GatingSync | undefined) => void }) {
  const apply = useApplyTicketedLive();
  const form = useForm<TicketedLiveFormValues>({ resolver: zodResolver(ticketedLiveFormSchema), defaultValues: ticketedDefaults });
  const { candidates, listOptions } = useScopeLookups();
  const [search, setSearch] = useState('');
  const liveOptions = useMemo(
    () => MonetizeUtils.searchMedia(candidates.filter((c) => c.live), new Set(), search, MONETIZE_CONSTRAINTS.MEDIA_SEARCH_MAX_OPTIONS),
    [candidates, search],
  );

  useEffect(() => {
    if (opened) form.reset(ticketedDefaults);
  }, [opened, form]);

  async function handleSubmit(v: TicketedLiveFormValues) {
    try {
      const res = await apply.mutateAsync({
        name: v.name.trim(),
        description: v.description.trim(),
        liveMediaId: v.liveMediaId,
        listIds: v.listIds.map((t) => String(MonetizeUtils.parseScope(t)?.id ?? '')).filter(Boolean),
        eventStartsAt: MonetizeUtils.pickerToIso(v.eventStartsAt),
        eventEndsAt: MonetizeUtils.pickerToIso(v.eventEndsAt),
        replayHours: v.replayHours,
        amount: MonetizeUtils.dollarsToCents(v.amount),
        promoCode: v.promoCode.trim().toUpperCase(),
        promoPercentOff: v.promoPercentOff,
      });
      notifications.show({ title: 'Event pass created', message: `"${v.name.trim()}" is on sale.`, color: 'green' });
      onApplied(res.gatingSync);
      onClose();
    } catch (err) {
      notifications.show({ title: 'Error', message: MonetizeUtils.errorMessage(err, 'Failed to create the event pass.'), color: 'red' });
    }
  }

  type C = Control<TicketedLiveFormValues>;
  const c = form.control as C;
  const promoCode = useWatch({ control: c, name: 'promoCode' });
  return (
    <Modal opened={opened} onClose={onClose} title="Ticketed live event" size="lg">
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextField control={c} name="name" label="Event name" description="For example “Homecoming vs. Lincoln” or “Spring Concert”" />
          <TextAreaField control={c} name="description" label="Description" />
          <Divider label="The event" labelPosition="left" />
          <LiveStreamField control={c} options={liveOptions} search={search} onSearch={setSearch} />
          <ListsField control={c} options={listOptions} />
          <Group grow>
            <TLDateField control={c} name="eventStartsAt" label="Starts" description="Empty = watch from purchase" />
            <TLDateField control={c} name="eventEndsAt" label="Ends" description="When the live event is over" />
          </Group>
          <ReplayField control={c} />
          <Divider label="Price and code" labelPosition="left" />
          <Group grow align="flex-start">
            <MoneyField control={c} name="amount" label="Pass price" />
            <TextField control={c} name="promoCode" label="Partner code (optional)" description="Letters and digits" upper />
            <PercentField control={c} disabled={promoCode === ''} />
          </Group>
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button type="submit" loading={apply.isPending}>Create event pass</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function LiveStreamField({ control, options, search, onSearch }: { control: Control<TicketedLiveFormValues>; options: { value: string; label: string }[]; search: string; onSearch: (s: string) => void }) {
  const { field, fieldState } = useController({ control, name: 'liveMediaId' });
  const selected = options.find((o) => o.value === field.value);
  return (
    <Select
      label="Live stream"
      withAsterisk
      placeholder="Type to search your live streams"
      data={selected || !field.value ? options : [...options, { value: field.value, label: `Stream #${field.value}` }]}
      searchable
      searchValue={search}
      onSearchChange={onSearch}
      filter={({ options: o }) => o}
      nothingFoundMessage="No live stream matches"
      value={field.value || null}
      onChange={(v) => field.onChange(v ?? '')}
      error={fieldState.error?.message}
    />
  );
}

function ListsField({ control, options }: { control: Control<TicketedLiveFormValues>; options: { value: string; label: string }[] }) {
  const { field } = useController({ control, name: 'listIds' });
  return <MultiSelect label="Also included (optional)" description="Playlists the pass unlocks too, for example a season archive" data={options} value={field.value} onChange={field.onChange} searchable clearable />;
}

function TLDateField({ control, name, label, description }: { control: Control<TicketedLiveFormValues>; name: 'eventStartsAt' | 'eventEndsAt'; label: string; description: string }) {
  const { field, fieldState } = useController({ control, name });
  return <DateTimeField label={label} description={description} clearable valueFormat="MMM D, YYYY h:mm A" value={field.value} onChange={(v) => field.onChange(v || null)} error={fieldState.error?.message} />;
}

function ReplayField({ control }: { control: Control<TicketedLiveFormValues> }) {
  const { field, fieldState } = useController({ control, name: 'replayHours' });
  return <NumberInput label="Replay window" description="How long after the event ends the recording stays available to pass holders" suffix=" hours" min={0} max={MONETIZE_CONSTRAINTS.MAX_REPLAY_HOURS} allowDecimal={false} value={field.value} onChange={(v) => field.onChange(typeof v === 'number' ? v : 0)} error={fieldState.error?.message} maw={240} />;
}

function PercentField({ control, disabled }: { control: Control<TicketedLiveFormValues>; disabled: boolean }) {
  const { field, fieldState } = useController({ control, name: 'promoPercentOff' });
  return <NumberInput label="Code takes off" suffix="%" min={0} max={100} allowDecimal={false} disabled={disabled} value={field.value} onChange={(v) => field.onChange(typeof v === 'number' ? v : 0)} error={fieldState.error?.message} />;
}

// ── Shared small fields ─────────────────────────────────────────

function TextField<T extends MembershipFormValues | TicketedLiveFormValues>({ control, name, label, description, upper }: { control: Control<T>; name: 'name' | 'promoCode'; label: string; description?: string; upper?: boolean }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { field, fieldState } = useController({ control: control as any, name });
  return (
    <TextInput
      label={label}
      description={description}
      withAsterisk={name === 'name'}
      maxLength={name === 'promoCode' ? MONETIZE_CONSTRAINTS.CODE_MAX : MONETIZE_CONSTRAINTS.NAME_MAX}
      value={field.value as string}
      onChange={(e) => field.onChange(upper ? e.currentTarget.value.toUpperCase() : e.currentTarget.value)}
      onBlur={field.onBlur}
      error={fieldState.error?.message}
    />
  );
}

function TextAreaField<T extends MembershipFormValues | TicketedLiveFormValues>({ control, name, label }: { control: Control<T>; name: 'description'; label: string }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { field, fieldState } = useController({ control: control as any, name });
  return <Textarea label={label} autosize minRows={2} maxLength={MONETIZE_CONSTRAINTS.DESCRIPTION_MAX} value={field.value as string} onChange={field.onChange} onBlur={field.onBlur} error={fieldState.error?.message} />;
}

function MoneyField<T extends MembershipFormValues | TicketedLiveFormValues>({ control, name, label }: { control: Control<T>; name: 'monthlyAmount' | 'yearlyAmount' | 'amount'; label: string }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { field, fieldState } = useController({ control: control as any, name });
  return <NumberInput label={label} prefix="$" decimalScale={2} fixedDecimalScale min={0} value={field.value as number | ''} onChange={field.onChange} error={fieldState.error?.message} />;
}
